import React, { useEffect, useRef } from 'react'
import * as styles from '../styles/components/pixel-animated-image.module.scss'
import cx from 'classnames'

export interface PixelAnimatedImageProps {
    imageSrc: string
    alt: string
    role?: React.AriaRole
    className?: string
}

const pixelationAmount = 300
const frameLengthMs = 42
const framesCount = 10

const drawOriginal = (image: HTMLImageElement, ctx: CanvasRenderingContext2D) => {
    ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, ctx.canvas.width, ctx.canvas.height)
}

const drawPixelated = (image: HTMLImageElement, ctx: CanvasRenderingContext2D, strength: number) => {
    const height = Math.ceil(image.height / strength)
    const width = Math.ceil(image.width / strength)

    ctx.imageSmoothingEnabled = false
    drawOriginal(image, ctx)

    // Resize canvas to size 'width' x 'height'
    // A 1px cropping is applied to all edges to remove the border appearing around some images due to color averaging
    ctx.drawImage(ctx.canvas, 0, 0, ctx.canvas.width, ctx.canvas.height, -1, -1, width + 2, height + 2)

    // Draw resized canvas onto original canvas, creating pixelated effect
    ctx.drawImage(ctx.canvas, 0, 0, width, height, 0, 0, ctx.canvas.width, ctx.canvas.height)
}

const animateTransition = (
    ctx: CanvasRenderingContext2D, image: HTMLImageElement, finalImg: HTMLImageElement, currentFrame: number,
) => {
    const strength = pixelationAmount - pixelationAmount * currentFrame/framesCount
    if (currentFrame === 1) {
        finalImg.style.display = 'block'
    }

    if (currentFrame < framesCount) {
        drawPixelated(image, ctx, strength)
        setTimeout(
            () =>
                requestAnimationFrame(() => animateTransition(ctx, image, finalImg, currentFrame + 1)),
            frameLengthMs,
        )
    } else {
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
    }
}

const PixelAnimatedImage = ({ imageSrc, alt }: PixelAnimatedImageProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const finalImgRef = useRef<HTMLImageElement>(null)

    useEffect(() => {
        const targetImage = new Image()
        targetImage.src = imageSrc
        const canvas = canvasRef.current
        const finalImg = finalImgRef.current

        if (!canvas || !finalImg) {
            return
        }

        targetImage.onload = () => {
            canvas.width = targetImage.naturalWidth
            canvas.height = targetImage.naturalHeight
            const ctx = canvas.getContext('2d')

            if (!ctx) {
                return
            }

            animateTransition(ctx, targetImage, finalImg, 0)
        }
    }, [imageSrc])

    return (
        <div className={styles.container}>
            <canvas ref={canvasRef} className={cx(styles.imageFrames)} />
            <img ref={finalImgRef} {...{ alt, src: imageSrc }} className={cx(styles.image)} />
        </div>
    )
}

export default PixelAnimatedImage
